import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import EnhancedSwipeCard from './discover/EnhancedSwipeCard';
import { Property } from '../types';

const SwipePreview: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [swipeDirection, setSwipeDirection] = useState<'left' | 'right' | null>(null);

  // Mock properties matching the real Property type
  const mockProperties: Property[] = [
    {
      id: '1',
      title: 'Modern Downtown Loft',
      address: '123 Main Street',
      city: 'Louisville',
      state: 'KY',
      zip_code: '40202',
      latitude: 38.2527,
      longitude: -85.7585,
      rent: 2400,
      deposit: 2400,
      bedrooms: 2,
      bathrooms: 2,
      square_feet: 1200,
      property_type: 'apartment',
      images: ['/lovable-uploads/49baeb2c-dad4-4893-a4c8-f06971fac101.png'],
      amenities: ['In-Unit Laundry', 'High-Speed Wi-Fi', 'Covered Parking', 'Fitness Center', 'Rooftop Terrace', 'Concierge Service'],
      available_date: '2024-02-15',
      description: 'Beautiful modern loft in the heart of downtown with stunning city views, high ceilings, and premium finishes throughout.',
      landlord_id: 'demo',
      created_at: '2024-01-01',
      updated_at: '2024-01-01',
      lease_length: 12,
      pet_policy: 'allowed',
      parking: true,
      utilities_included: ['Water', 'Trash'],
      status: 'active',
      view_count: 0,
      inquiry_count: 0,
      application_count: 0
    },
    {
      id: '2',
      title: 'Cozy Garden Apartment',
      address: '456 Oak Avenue',
      city: 'Louisville',
      state: 'KY',
      zip_code: '40204',
      latitude: 38.2389,
      longitude: -85.7249,
      rent: 1800,
      deposit: 1800,
      bedrooms: 1,
      bathrooms: 1,
      square_feet: 850,
      property_type: 'apartment',
      images: ['/lovable-uploads/036c702f-9450-4758-8105-bf4f2c2cf3c5.png'],
      amenities: ['Garden Access', 'High-Speed Wi-Fi', 'Pet-Friendly', 'Private Balcony', 'On-Site Laundry', 'Storage Unit'],
      available_date: '2024-03-01',
      description: 'Charming apartment with garden access, perfect for those who love nature.',
      landlord_id: 'demo',
      created_at: '2024-01-01',
      updated_at: '2024-01-01',
      lease_length: 12,
      pet_policy: 'allowed',
      parking: true,
      utilities_included: ['Water'],
      status: 'active',
      view_count: 0,
      inquiry_count: 0,
      application_count: 0
    },
    {
      id: '3',
      title: 'Luxury High-Rise Suite',
      address: '789 River Drive',
      city: 'Louisville',
      state: 'KY',
      zip_code: '40206',
      latitude: 38.2562,
      longitude: -85.7044,
      rent: 3200,
      deposit: 3200,
      bedrooms: 3,
      bathrooms: 2,
      square_feet: 1650,
      property_type: 'condo',
      images: ['/lovable-uploads/21767fd8-04ff-4dfb-9104-72c52bebd70b.png'],
      amenities: ['Valet Parking', 'High-Speed Wi-Fi', 'Fitness Center', 'Swimming Pool', '24/7 Concierge', 'Business Center', 'Spa Services', 'Wine Storage'],
      available_date: '2024-02-20',
      description: 'Elegant suite with panoramic river views, premium amenities, and 24/7 concierge service.',
      landlord_id: 'demo',
      created_at: '2024-01-01',
      updated_at: '2024-01-01',
      lease_length: 12,
      pet_policy: 'not_allowed',
      parking: true,
      utilities_included: ['Water', 'Trash', 'Electric', 'Gas', 'Internet'],
      status: 'active',
      view_count: 0,
      inquiry_count: 0,
      application_count: 0
    }
  ];

  const currentProperty = mockProperties[currentIndex];
  const nextProperty = mockProperties[(currentIndex + 1) % mockProperties.length];

  // Match scores
  const currentMatchScore = currentIndex === 0 ? 93 : currentIndex === 1 ? 96 : 99;
  const nextMatchScore = (currentIndex + 1) % 3 === 0 ? 93 : (currentIndex + 1) % 3 === 1 ? 96 : 99;

  const handleSwipe = useCallback((direction: 'left' | 'right') => {
    setSwipeDirection(direction);
    
    setTimeout(() => {
      setCurrentIndex((prev) => (prev + 1) % mockProperties.length);
      setSwipeDirection(null);
    }, 350);
  }, [mockProperties.length]);

  return (
    <div className="relative w-full h-[75vh] md:h-[85vh] max-w-md md:max-w-2xl lg:max-w-4xl mx-auto">
      <AnimatePresence mode="wait">
        {/* Next Card (Background) - subtle depth effect */}
        {nextProperty && (
          <motion.div
            key={`next-${nextProperty.id}`}
            initial={{ scale: 0.92, opacity: 0, y: 10 }}
            animate={{ scale: 0.96, opacity: 0.4, y: 8 }}
            exit={{ scale: 0.92, opacity: 0, y: 10 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="absolute inset-0 pointer-events-none z-0"
          >
            <EnhancedSwipeCard
              property={nextProperty}
              matchScore={nextMatchScore}
              className="w-full h-full"
              isNext={true}
            />
          </motion.div>
        )}
        
        {/* Current Card (Foreground) - smooth entrance */}
        {currentProperty && (
          <motion.div
            key={`current-${currentProperty.id}`}
            initial={{ scale: 0.92, opacity: 0, y: 10 }}
            animate={{
              scale: 1,
              opacity: 1,
              y: 0,
              transition: {
                duration: 0.3,
                type: "spring",
                stiffness: 260,
                damping: 25
              }
            }}
            exit={{
              scale: 0.9,
              opacity: 0,
              x: swipeDirection === 'right' ? 400 : swipeDirection === 'left' ? -400 : 0,
              rotate: swipeDirection === 'right' ? 20 : swipeDirection === 'left' ? -20 : 0,
              transition: {
                duration: 0.3,
                ease: "easeOut"
              }
            }}
            className="relative z-10 w-full h-full"
          >
            <EnhancedSwipeCard
              property={currentProperty}
              matchScore={currentMatchScore}
              onSwipe={handleSwipe}
              className="w-full h-full"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SwipePreview;
